import { useEffect, useMemo, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import {
  Plus,
  Eye,
  Loader2,
  PackageSearch,
  ArrowDownToLine
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2
}).format(amount || 0);

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const statusColors = {
  draft: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-blue-100 text-blue-800',
  partial: 'bg-purple-100 text-purple-800',
  received: 'bg-green-100 text-green-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800'
};

export default function PurchaseOrdersPage() {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [rawItems, setRawItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);

  const [selectedPO, setSelectedPO] = useState(null);
  const [poReceipts, setPoReceipts] = useState([]);

  const [poForm, setPoForm] = useState({
    vendor_id: '',
    order_date: '',
    expected_date: '',
    notes: ''
  });
  const [poItems, setPoItems] = useState([{ raw_item_id: '', qty_kg: '', rate_per_kg: '' }]);

  const [receiveItems, setReceiveItems] = useState([{ raw_item_id: '', bags_count: '', kg_per_bag: '', warehouse_id: '', note: '' }]);
  const [receiveNote, setReceiveNote] = useState('');
  const [receiveDate, setReceiveDate] = useState('');
  const [markCompleted, setMarkCompleted] = useState(false);
  const [completionNote, setCompletionNote] = useState('');

  useEffect(() => {
    fetchInitial();
  }, []);

  const fetchInitial = async () => {
    setLoading(true);
    try {
      const [poRes, vendorRes, rawRes, warehouseRes] = await Promise.all([
        adminApi.getPurchaseOrders(),
        adminApi.getVendors(),
        adminApi.getRawItems(),
        adminApi.getWarehouses()
      ]);
      const poItems = Array.isArray(poRes.data) ? poRes.data : (poRes.data?.items || []);
      setPurchaseOrders(poItems);
      setVendors(vendorRes.data || []);
      setRawItems(rawRes.data || []);
      setWarehouses(warehouseRes.data || []);
    } catch (error) {
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const resetPoForm = () => {
    setPoForm({ vendor_id: '', order_date: '', expected_date: '', notes: '' });
    setPoItems([{ raw_item_id: '', qty_kg: '', rate_per_kg: '' }]);
  };

  const handleAddPoItem = () => {
    setPoItems((prev) => [...prev, { raw_item_id: '', qty_kg: '', rate_per_kg: '' }]);
  };

  const handleRemovePoItem = (index) => {
    setPoItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreatePO = async () => {
    if (!poForm.vendor_id) {
      toast.error('Select a vendor');
      return;
    }
    const hasInvalid = poItems.some((item) => !item.raw_item_id || !item.qty_kg || !item.rate_per_kg);
    if (hasInvalid) {
      toast.error('Fill all item details');
      return;
    }

    setSaving(true);
    try {
      await adminApi.createPurchaseOrder({
        vendor_id: poForm.vendor_id,
        order_date: poForm.order_date || undefined,
        expected_date: poForm.expected_date || undefined,
        notes: poForm.notes || undefined,
        items: poItems.map((item) => ({
          raw_item_id: item.raw_item_id,
          qty_kg: parseFloat(item.qty_kg),
          rate_per_kg: parseFloat(item.rate_per_kg)
        }))
      });
      toast.success('Purchase order created');
      setShowCreateModal(false);
      resetPoForm();
      fetchInitial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PO');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (po) => {
    setShowDetailModal(true);
    try {
      const [detailRes, receiptsRes] = await Promise.all([
        adminApi.getPurchaseOrderById(po.id),
        adminApi.getPurchaseOrderReceipts(po.id)
      ]);
      setSelectedPO(detailRes.data);
      setPoReceipts(receiptsRes.data || []);
    } catch (error) {
      toast.error('Failed to load PO details');
    }
  };

  const openReceive = () => {
    if (!selectedPO) return;
    if (['completed', 'cancelled'].includes(selectedPO.status)) {
      toast.error(`Cannot receive a PO in '${selectedPO.status}' status`);
      return;
    }
    setReceiveItems([{ raw_item_id: '', bags_count: '', kg_per_bag: '', warehouse_id: '', note: '' }]);
    setReceiveNote('');
    setReceiveDate('');
    setMarkCompleted(false);
    setCompletionNote('');
    setShowReceiveModal(true);
  };

  const handleAddReceiveItem = () => {
    setReceiveItems((prev) => [...prev, { raw_item_id: '', bags_count: '', kg_per_bag: '', warehouse_id: '', note: '' }]);
  };

  const handleRemoveReceiveItem = (index) => {
    setReceiveItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReceivePO = async () => {
    if (!selectedPO) return;
    const invalid = receiveItems.some((item) => !item.raw_item_id || !item.bags_count || !item.kg_per_bag || !item.warehouse_id);
    if (invalid) {
      toast.error('Fill all receipt items');
      return;
    }
    if (markCompleted && !completionNote.trim()) {
      toast.error('Add completion remarks before marking PO as completed');
      return;
    }

    setSaving(true);
    try {
      await adminApi.receivePurchaseOrder(selectedPO.id, {
        received_date: receiveDate || undefined,
        note: receiveNote || undefined,
        mark_as_completed: markCompleted,
        completion_note: markCompleted ? completionNote.trim() : undefined,
        items: receiveItems.map((item) => ({
          raw_item_id: item.raw_item_id,
          bags_count: parseInt(item.bags_count, 10),
          kg_per_bag: parseFloat(item.kg_per_bag),
          warehouse_id: item.warehouse_id,
          note: item.note || undefined
        }))
      });
      toast.success('PO received');
      setShowReceiveModal(false);
      openDetail({ id: selectedPO.id });
      fetchInitial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive PO');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPOCompleted = async () => {
    if (!selectedPO) return;
    if (['completed', 'cancelled'].includes(selectedPO.status)) return;

    const ok = window.confirm('Mark this PO as completed? You will not be able to receive more stock for this PO.');
    if (!ok) return;

    setSaving(true);
    try {
      await adminApi.updatePurchaseOrderStatus(selectedPO.id, { status: 'completed' });
      toast.success('PO marked as completed');
      await Promise.all([
        openDetail({ id: selectedPO.id }),
        fetchInitial()
      ]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark PO as completed');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const res = await adminApi.downloadAdminDatasetExcel('purchase-orders');
      downloadBlob(res.data, 'purchase-orders.xlsx');
    } catch (error) {
      toast.error('Failed to download Excel');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleCreatePaymentVoucher = async () => {
    if (!selectedPO) return;
    const pending = Number(selectedPO.pending_vendor_amount || 0);
    if (pending <= 0) {
      toast.error('No pending vendor amount for this PO');
      return;
    }

    const ok = window.confirm(`Create vendor payment voucher for pending amount ${formatCurrency(pending)}?`);
    if (!ok) return;

    setSaving(true);
    try {
      const response = await adminApi.createPurchaseOrderPaymentVoucher(selectedPO.id, {});
      toast.success(`Payment voucher created: ${response?.data?.voucher_no || ''}`.trim());
      await Promise.all([
        openDetail({ id: selectedPO.id }),
        fetchInitial()
      ]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment voucher');
    } finally {
      setSaving(false);
    }
  };

  const rawItemOptions = useMemo(() => rawItems.filter((r) => r.is_active), [rawItems]);
  const vendorOptions = useMemo(() => vendors.filter((v) => v.is_active), [vendors]);
  const warehouseOptions = useMemo(() => warehouses.filter((w) => w.is_active), [warehouses]);

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <LoadingOverlay open={downloadingExcel} message="Please wait. Report is being generated" />
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Purchase Orders</h1>
        <p className="text-[#A8A8A8] mt-1">Create and receive raw material purchase orders</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#2D2420]">PO List</h2>
              <p className="text-sm text-[#A8A8A8]">Track PO statuses and receipts</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleDownloadExcel}>
                Download Excel
              </Button>
              <Button onClick={() => { resetPoForm(); setShowCreateModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Create PO
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : purchaseOrders.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-[#A8A8A8]">
              <PackageSearch className="w-6 h-6 mr-2" />
              No purchase orders yet
            </div>
          ) : (
            <div className="overflow-x-auto mt-4">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">PO No</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Vendor</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Order Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Total</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Pending</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {purchaseOrders.map((po) => (
                    <tr key={po.id} className="hover:bg-[#FAFAF9]/60">
                      <td className="py-3 px-4 text-[#2D2420] font-medium">{po.po_no}</td>
                      <td className="py-3 px-4 text-[#2D2420]">{po.vendor_name}</td>
                      <td className="py-3 px-4 text-[#A8A8A8]">{formatDate(po.order_date)}</td>
                      <td className="py-3 px-4 text-[#2D2420]">{formatCurrency(po.total_amount)}</td>
                      <td className="py-3 px-4 text-[#2D2420]">{formatCurrency(po.pending_vendor_amount || 0)}</td>
                      <td className="py-3 px-4">
                        <Badge className={cn('text-xs', statusColors[po.status] || 'bg-gray-100 text-gray-800')}>
                          {po.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end">
                          <Button variant="ghost" size="icon" onClick={() => openDetail(po)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Create Purchase Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Vendor</Label>
                <Select value={poForm.vendor_id} onValueChange={(value) => setPoForm((prev) => ({ ...prev, vendor_id: value }))}>
                  <SelectTrigger className="border-[#E5E5E5]">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendorOptions.map((vendor) => (
                      <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Order Date</Label>
                <Input type="date" value={poForm.order_date} onChange={(e) => setPoForm((prev) => ({ ...prev, order_date: e.target.value }))} className="border-[#E5E5E5]" />
              </div>
              <div>
                <Label>Expected Date</Label>
                <Input type="date" value={poForm.expected_date} onChange={(e) => setPoForm((prev) => ({ ...prev, expected_date: e.target.value }))} className="border-[#E5E5E5]" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={poForm.notes} onChange={(e) => setPoForm((prev) => ({ ...prev, notes: e.target.value }))} className="border-[#E5E5E5]" />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#2D2420]">Items</h3>
                <Button variant="outline" onClick={handleAddPoItem}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </div>
              {poItems.map((item, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label>Raw Item</Label>
                    <Select value={item.raw_item_id} onValueChange={(value) => {
                      const next = [...poItems];
                      next[index].raw_item_id = value;
                      setPoItems(next);
                    }}>
                      <SelectTrigger className="border-[#E5E5E5]">
                        <SelectValue placeholder="Select raw item" />
                      </SelectTrigger>
                      <SelectContent>
                        {rawItemOptions.map((raw) => (
                          <SelectItem key={raw.id} value={raw.id}>{raw.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Qty (kg)</Label>
                    <Input type="number" value={item.qty_kg} onChange={(e) => {
                      const next = [...poItems];
                      next[index].qty_kg = e.target.value;
                      setPoItems(next);
                    }} className="border-[#E5E5E5]" />
                  </div>
                  <div>
                    <Label>Rate/kg</Label>
                    <Input type="number" value={item.rate_per_kg} onChange={(e) => {
                      const next = [...poItems];
                      next[index].rate_per_kg = e.target.value;
                      setPoItems(next);
                    }} className="border-[#E5E5E5]" />
                  </div>
                  <div className="flex items-end">
                    {poItems.length > 1 && (
                      <Button variant="outline" onClick={() => handleRemovePoItem(index)}>Remove</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreatePO} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">PO Details</DialogTitle>
          </DialogHeader>
          {!selectedPO ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[#2D2420]">{selectedPO.po_no}</p>
                  <p className="text-sm text-[#A8A8A8]">{selectedPO.vendor_name}</p>
                </div>
                <Badge className={cn('text-xs', statusColors[selectedPO.status] || 'bg-gray-100 text-gray-800')}>
                  {selectedPO.status}
                </Badge>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-[#5C4033] uppercase">Raw Item</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-[#5C4033] uppercase">Qty (kg)</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-[#5C4033] uppercase">Received (kg)</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-[#5C4033] uppercase">Rate/kg</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-[#5C4033] uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E5]">
                    {selectedPO.items.map((item, idx) => (
                      <tr key={`${item.raw_item_id}-${idx}`}>
                        <td className="py-2 px-3 text-[#2D2420]">{item.raw_item_name}</td>
                        <td className="py-2 px-3 text-right text-[#2D2420]">{item.qty_kg}</td>
                        <td className="py-2 px-3 text-right text-[#2D2420]">{item.received_kg || 0}</td>
                        <td className="py-2 px-3 text-right text-[#2D2420]">{formatCurrency(item.rate_per_kg)}</td>
                        <td className="py-2 px-3 text-right text-[#2D2420]">{formatCurrency(item.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-[#A8A8A8]">
                  Total: {formatCurrency(selectedPO.total_amount)} • Paid: {formatCurrency(selectedPO.vendor_paid_amount || 0)} • Pending: {formatCurrency(selectedPO.pending_vendor_amount || 0)}
                </div>
                <div className="flex items-center gap-2">
                  {Number(selectedPO.pending_vendor_amount || 0) > 0 && selectedPO.status !== 'cancelled' && (
                    <Button variant="outline" onClick={handleCreatePaymentVoucher} disabled={saving}>
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Create Payment Voucher
                    </Button>
                  )}
                  {!['completed', 'cancelled'].includes(selectedPO.status) && (
                    <Button variant="outline" onClick={handleMarkPOCompleted} disabled={saving}>
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Mark PO as Completed
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={openReceive}
                    disabled={['completed', 'cancelled'].includes(selectedPO.status)}
                  >
                    <ArrowDownToLine className="w-4 h-4 mr-2" />
                    Receive
                  </Button>
                </div>
              </div>

              {poReceipts.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-[#2D2420]">Receipts</p>
                  <div className="space-y-2">
                    {poReceipts.map((receipt) => (
                      <div key={receipt.id} className="rounded-lg border border-[#E5E5E5] p-3">
                        <p className="text-sm text-[#2D2420]">Received: {formatDate(receipt.received_date)}</p>
                        <p className="text-xs text-[#A8A8A8]">{receipt.note || '-'}</p>
                        <div className="mt-2 text-xs text-[#5C4033]">
                          {receipt.items.map((item, idx) => (
                            <div key={`${item.raw_item_id}-${idx}`}>
                              {item.raw_item_name} • {item.received_kg} kg • {item.bags_count} bags
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showReceiveModal} onOpenChange={setShowReceiveModal}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Receive PO</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Received Date</Label>
                <Input type="date" value={receiveDate} onChange={(e) => setReceiveDate(e.target.value)} className="border-[#E5E5E5]" />
              </div>
              <div>
                <Label>Note</Label>
                <Input value={receiveNote} onChange={(e) => setReceiveNote(e.target.value)} className="border-[#E5E5E5]" />
              </div>
            </div>

            <div className="rounded-md border border-[#E5E5E5] p-3 space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="mark-po-completed"
                  checked={markCompleted}
                  onCheckedChange={(checked) => setMarkCompleted(checked === true)}
                />
                <Label htmlFor="mark-po-completed" className="cursor-pointer">
                  Mark this PO as completed (even if received qty is less)
                </Label>
              </div>
              {markCompleted && (
                <div>
                  <Label>Completion Remarks</Label>
                  <Textarea
                    value={completionNote}
                    onChange={(e) => setCompletionNote(e.target.value)}
                    placeholder="Example: 2 bags damaged in transit, accepted short quantity and closed PO."
                    className="border-[#E5E5E5]"
                  />
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#2D2420]">Receipt Items</h3>
                <Button variant="outline" onClick={handleAddReceiveItem}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </div>
              {receiveItems.map((item, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div>
                    <Label>Raw Item</Label>
                    <Select value={item.raw_item_id} onValueChange={(value) => {
                      const next = [...receiveItems];
                      next[index].raw_item_id = value;
                      setReceiveItems(next);
                    }}>
                      <SelectTrigger className="border-[#E5E5E5]">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {rawItemOptions.map((raw) => (
                          <SelectItem key={raw.id} value={raw.id}>{raw.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Bags</Label>
                    <Input type="number" value={item.bags_count} onChange={(e) => {
                      const next = [...receiveItems];
                      next[index].bags_count = e.target.value;
                      setReceiveItems(next);
                    }} className="border-[#E5E5E5]" />
                  </div>
                  <div>
                    <Label>KG/Bag</Label>
                    <Input type="number" value={item.kg_per_bag} onChange={(e) => {
                      const next = [...receiveItems];
                      next[index].kg_per_bag = e.target.value;
                      setReceiveItems(next);
                    }} className="border-[#E5E5E5]" />
                  </div>
                  <div>
                    <Label>Warehouse</Label>
                    <Select value={item.warehouse_id} onValueChange={(value) => {
                      const next = [...receiveItems];
                      next[index].warehouse_id = value;
                      setReceiveItems(next);
                    }}>
                      <SelectTrigger className="border-[#E5E5E5]">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouseOptions.map((wh) => (
                          <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    {receiveItems.length > 1 && (
                      <Button variant="outline" onClick={() => handleRemoveReceiveItem(index)}>Remove</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowReceiveModal(false)}>Cancel</Button>
            <Button onClick={handleReceivePO} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Receive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
